import AbstractAccountVisibility from "./AbstractAccountVisibility.js";

/**
 * This visibility class is used by the budget editor to make certain
 * account rows visible even if they contain no data.
 *
 * The receiver stores an array of account numbers whose rows should
 * always be visible: i.e., if an account row is attached to an
 * account number stored in this array, the row will be visible. The
 * visibility of other rows is decided by another visibility object,
 * implementing a Chain of Responsibility design pattern.
 *
 * New account numbers can be added with `addAlwaysVisibleAccountNumber()`.
 *
 * @param {AbstractAccountVisibility} next - The "next"
 * visibiliy object in the chain of responsibility. If an account row
 * isn't attached to an account number stored in the always visible
 * account numbers array, this visibility object should decide if the
 * row is visible or not.
 */
export default class BudgetReportAccountRowVisibility extends AbstractAccountVisibility {
	constructor({ next } = {}) {
		super(...arguments);

		this._next = next;

		this._alwaysVisibleAccountNumbers = [];
	}

	/**
	 * Make account rows for `accountNumber` always visible.
	 */
	addAlwaysVisibleAccountNumber(accountNumber) {
		this._alwaysVisibleAccountNumbers = [
			...this._alwaysVisibleAccountNumbers,
			accountNumber,
		];
	}

	/**
	 * Change the next visibility in the chain of responsibility.
	 */
	setNext(visibility) {
		this._next = visibility;
	}

	isVisible(row) {
		return this._shouldMakeRowVisible(row) || this._next.isVisible(row);
	}

	matches(visibility) {
		return super.matches(visibility) || this._next.matches(visibility);
	}

	_shouldMakeRowVisible(row) {
		if (this._alwaysVisibleAccountNumbers.length === 0) {
			return false;
		}

		return (
			this._includesRow(row) ||
			// if one of the children of `row` should be made visible, all
			// its ancestors should as well:
			this._includesChildOfRow(row)
		);
	}

	_includesRow(row) {
		if (!row.isAccountRow()) {
			return false;
		}

		const accountNumber = row.getAccountNumber();

		return this._alwaysVisibleAccountNumbers.includes(accountNumber);
	}

	_includesChildOfRow(row) {
		return row.getRows().find((child) => this._shouldMakeRowVisible(child));
	}
}
